/**
 * useLensEvaluation — GraphQL hook for 4-phase lens evaluation (DRR-075).
 *
 * Evaluates the lens chain for a given actor and returns which
 * entities/fields are visible after narrowing.
 */

import { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL Operations
// ---------------------------------------------------------------------------

const EVALUATE_LENS_QUERY = gql`
  query EvaluateLens($input: EvaluateLensInput!) {
    evaluateLens(input: $input) {
      phases {
        phase
        narrowed
        widened
        triggeredRuleId
        durationUs
        breakGlassEventId
        remainingBreakGlassUses
      }
      visibleEntityIds
      visibleFields
      hiddenFields
      breakGlassActivated
      totalDurationUs
      withinLatencyTarget
    }
  }
`;

const ACTIVE_LENSES_QUERY = gql`
  query ActiveLenses {
    activeLenses {
      lensId
      name
      phase
      safetyLevel
      vertical
    }
  }
`;

const SETTING_BUNDLE_QUERY = gql`
  query SettingBundle {
    settingBundle {
      settingId
      name
      codeSystem
      code
      industryLabel
      subjectLabel
      regulatoryFrameworks
      features
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LensPhaseResult {
  phase: string;
  narrowed: boolean;
  widened: boolean;
  triggeredRuleId: string | null;
  durationUs: number;
  breakGlassEventId: string | null;
  remainingBreakGlassUses: number | null;
}

export interface LensEvaluationResult {
  phases: LensPhaseResult[];
  visibleEntityIds: string[];
  visibleFields: string[];
  hiddenFields: string[];
  breakGlassActivated: boolean;
  totalDurationUs: number;
  withinLatencyTarget: boolean;
}

export interface ActiveLens {
  lensId: string;
  name: string;
  phase: string;
  safetyLevel: string;
  vertical: string;
}

export interface SettingBundle {
  settingId: string;
  name: string;
  codeSystem: string;
  code: string;
  industryLabel: string;
  subjectLabel: string;
  regulatoryFrameworks: string[];
  features: string[];
}

export interface EvaluateLensInput {
  actorId: string;
  entityIds?: string[];
  breakGlassJustification?: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseLensEvaluationReturn {
  evaluation: LensEvaluationResult | null;
  activeLenses: ActiveLens[];
  settingBundle: SettingBundle | null;
  isEvaluating: boolean;
  error: Error | null;
  evaluateLens: (input: EvaluateLensInput) => Promise<void>;
  fetchActiveLenses: () => Promise<void>;
  fetchSettingBundle: () => Promise<void>;
}

export function useLensEvaluation(): UseLensEvaluationReturn {
  const [evaluation, setEvaluation] =
    useState<LensEvaluationResult | null>(null);
  const [activeLenses, setActiveLenses] = useState<ActiveLens[]>([]);
  const [settingBundle, setSettingBundle] =
    useState<SettingBundle | null>(null);

  const [evaluateLensQuery, { loading: evaluating, error: evalError }] =
    useLazyQuery<{ evaluateLens: LensEvaluationResult }>(
      EVALUATE_LENS_QUERY
    );

  const [fetchLensesQuery] = useLazyQuery<{
    activeLenses: ActiveLens[];
  }>(ACTIVE_LENSES_QUERY);

  const [fetchSettingQuery] = useLazyQuery<{
    settingBundle: SettingBundle;
  }>(SETTING_BUNDLE_QUERY);

  const evaluateLens = useCallback(
    async (input: EvaluateLensInput) => {
      try {
        const { data } = await evaluateLensQuery({
          variables: { input },
        });
        if (data?.evaluateLens) {
          setEvaluation(data.evaluateLens);
        }
      } catch (err) {
        // AbortError is expected when component unmounts during query — not an error
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('Lens evaluation failed:', err);
      }
    },
    [evaluateLensQuery]
  );

  const fetchActiveLenses = useCallback(async () => {
    try {
      const { data } = await fetchLensesQuery();
      if (data?.activeLenses) {
        setActiveLenses(data.activeLenses);
      }
    } catch (err) {
      // AbortError is expected when component unmounts during query — not an error
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Failed to fetch active lenses:', err);
    }
  }, [fetchLensesQuery]);

  const fetchSettingBundle = useCallback(async () => {
    try {
      const { data } = await fetchSettingQuery();
      if (data?.settingBundle) {
        setSettingBundle(data.settingBundle);
      }
    } catch (err) {
      console.error('Failed to fetch setting bundle:', err);
    }
  }, [fetchSettingQuery]);

  return {
    evaluation,
    activeLenses,
    settingBundle,
    isEvaluating: evaluating,
    error: evalError ?? null,
    evaluateLens,
    fetchActiveLenses,
    fetchSettingBundle,
  };
}
